import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Handshake, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatINR } from "@/lib/utils";
import { AgreementStatusBadge } from "@/components/agreements/AgreementStatusBadge";
import { NewAgreementDialog } from "@/components/agreements/NewAgreementDialog";
import type { AgreementListRow } from "@/types/agreement";

interface DealAgreementsCardProps {
  dealId: string;
  dealName: string;
  isClosed: boolean;
}

/** The deal's agreements: the contract made from its accepted quotation */
export function DealAgreementsCard({ dealId, dealName, isClosed }: DealAgreementsCardProps) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState<AgreementListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);

  const canRead = hasPermission("agreement.read");
  const canCreate = hasPermission("agreement.create") && !isClosed;

  useEffect(() => {
    if (!canRead) return;
    setLoading(true);
    api
      .get(`/api/Agreement/List?deal_id=${dealId}&page_size=100`)
      .then((res) => setRows(unwrapPage<AgreementListRow>(res).rows))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [dealId, canRead]);

  if (!canRead) return null;

  // Superseded versions are reachable from the newer version
  const visible = rows.filter((r) => r.agreement_status !== "SUPERSEDED");

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Handshake className="size-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">Agreements</h3>
          {visible.length > 0 && <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground">{visible.length}</span>}
        </div>
        {canCreate && (
          <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} className="h-8 gap-1.5 text-xs">
            <Plus className="size-3.5" />
            New Agreement
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <div className="py-5 px-4 text-center text-xs text-muted-foreground">
          No agreements yet.
          {canCreate ? " Once the customer accepts the quotation, make the agreement from it." : ""}
        </div>
      ) : (
        <ul className="divide-y">
          {visible.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => navigate(`/agreements/${r.id}`)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/50">
                <div className="min-w-0">
                  <div className="font-mono text-xs font-semibold">{r.display_number}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{r.title}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold">{formatINR(r.contract_value)}</span>
                  <AgreementStatusBadge status={r.agreement_status} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <NewAgreementDialog open={newOpen} onOpenChange={setNewOpen} dealId={dealId} dealName={dealName} />
    </div>
  );
}
